import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, Plus, Trash2, Users, Loader2, Percent, Pencil, Zap } from 'lucide-react';
import SearchableDriverSelect from './SearchableDriverSelect';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tool to record team-driver pay transfers for a pay period.
 * A transfer moves a manual lump sum from a primary driver (deducted) to a
 * co-driver (credited) on the payroll run for the same week.
 *
 * Props: weekStart, weekEnd (YYYY-MM-DD) — must match the payroll being generated.
 */
const PayTransfersPanel = ({ weekStart, weekEnd }) => {
  const [drivers, setDrivers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestHint, setSuggestHint] = useState('');
  const [coPercent, setCoPercent] = useState('50');
  const [savingPreset, setSavingPreset] = useState(false);
  const [presets, setPresets] = useState([]);
  const [autoFilling, setAutoFilling] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [qaSaving, setQaSaving] = useState(false);
  const [qa, setQa] = useState({ from_driver_id: '', to_driver_id: '', co_percent: '50' });
  const [form, setForm] = useState({ from_driver_id: '', to_driver_id: '', amount: '', note: '' });

  const fetchPresets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pay-transfers/split-presets`, { withCredentials: true });
      setPresets(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/drivers`, { withCredentials: true });
        setDrivers(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchTransfers = useCallback(async () => {
    if (!weekStart || !weekEnd) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pay-transfers`, {
        params: { week_start: weekStart, week_end: weekEnd },
        withCredentials: true,
      });
      setTransfers(r.data || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);
  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  // Load the saved per-pair default split whenever both drivers are chosen.
  useEffect(() => {
    if (!form.from_driver_id || !form.to_driver_id) return;
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/pay-transfers/split-preset`, {
          params: { from_driver_id: form.from_driver_id, to_driver_id: form.to_driver_id },
          withCredentials: true,
        });
        if (mounted && r.data?.co_percent != null) setCoPercent(String(r.data.co_percent));
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [form.from_driver_id, form.to_driver_id]);

  const addTransfer = async () => {
    if (!form.from_driver_id || !form.to_driver_id) {
      toast.error('Pick both a primary driver and a co-driver');
      return;
    }
    if (form.from_driver_id === form.to_driver_id) {
      toast.error('Transfer must be between two different drivers');
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a transfer amount greater than 0');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/pay-transfers`, {
        week_start: weekStart,
        week_end: weekEnd,
        from_driver_id: form.from_driver_id,
        to_driver_id: form.to_driver_id,
        amount: amt,
        note: form.note || null,
      }, { withCredentials: true });
      toast.success('Pay transfer added');
      setForm({ from_driver_id: '', to_driver_id: '', amount: '', note: '' });
      setSuggestHint('');
      fetchTransfers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add transfer');
    } finally {
      setBusy(false);
    }
  };

  const suggestSplit = async () => {
    if (!form.from_driver_id || !form.to_driver_id) {
      toast.error('Pick both a primary driver and a co-driver first');
      return;
    }
    setSuggesting(true);
    try {
      const r = await axios.get(`${API}/pay-transfers/suggested-split`, {
        params: {
          week_start: weekStart,
          week_end: weekEnd,
          from_driver_id: form.from_driver_id,
          to_driver_id: form.to_driver_id,
          co_percent: coPercent === '' ? undefined : Number(coPercent),
        },
        withCredentials: true,
      });
      const { team_load_count, team_loads_gross, suggested_amount, co_percent } = r.data || {};
      const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const pctLabel = `${Number(co_percent)}/${100 - Number(co_percent)}`;
      if (!team_load_count) {
        setSuggestHint('No team loads for these two drivers in this period.');
        toast.info('No team loads found for these drivers this week');
      } else {
        setForm((f) => ({ ...f, amount: String(suggested_amount) }));
        setSuggestHint(`${team_load_count} team load${team_load_count === 1 ? '' : 's'} · primary pay $${fmt(team_loads_gross)} → ${pctLabel} split = $${fmt(suggested_amount)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to compute split');
    } finally {
      setSuggesting(false);
    }
  };

  const savePreset = async () => {
    if (!form.from_driver_id || !form.to_driver_id) {
      toast.error('Pick both drivers first');
      return;
    }
    const pct = Number(coPercent);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Co-driver share must be between 0 and 100');
      return;
    }
    setSavingPreset(true);
    try {
      await axios.put(`${API}/pay-transfers/split-preset`, {
        from_driver_id: form.from_driver_id,
        to_driver_id: form.to_driver_id,
        co_percent: pct,
      }, { withCredentials: true });
      toast.success(`Saved default: co-driver gets ${pct}%`);
      fetchPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save default split');
    } finally {
      setSavingPreset(false);
    }
  };

  const editPreset = (p) => {
    setForm((f) => ({ ...f, from_driver_id: p.from_driver_id, to_driver_id: p.to_driver_id }));
    setCoPercent(String(p.co_percent));
    setSuggestHint('');
  };

  const deletePreset = async (id) => {
    if (!window.confirm('Remove this saved default split?')) return;
    try {
      await axios.delete(`${API}/pay-transfers/split-presets/${id}`, { withCredentials: true });
      fetchPresets();
    } catch {
      toast.error('Failed to remove default split');
    }
  };

  const removeTransfer = async (id) => {
    if (!window.confirm('Remove this pay transfer?')) return;
    try {
      await axios.delete(`${API}/pay-transfers/${id}`, { withCredentials: true });
      fetchTransfers();
    } catch {
      toast.error('Failed to remove transfer');
    }
  };

  const autoFill = async () => {
    setAutoFilling(true);
    try {
      const r = await axios.post(`${API}/pay-transfers/auto-create`, {
        week_start: weekStart, week_end: weekEnd,
      }, { withCredentials: true });
      const { created, skipped_zero } = r.data || {};
      if (created) {
        toast.success(`Created ${created} transfer${created === 1 ? '' : 's'} from saved splits${skipped_zero ? ` (${skipped_zero} skipped — no pay set)` : ''}`);
        fetchTransfers();
      } else if (skipped_zero) {
        toast.info(`Nothing created — ${skipped_zero} pair(s) had $0 pay (no pay profile).`);
      } else {
        toast.info('All team loads already have a transfer for this week.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fill failed');
    } finally {
      setAutoFilling(false);
    }
  };

  const saveQuickPreset = async () => {
    if (!qa.from_driver_id || !qa.to_driver_id) {
      toast.error('Pick both drivers');
      return;
    }
    if (qa.from_driver_id === qa.to_driver_id) {
      toast.error('Pick two different drivers');
      return;
    }
    const pct = Number(qa.co_percent);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Co-driver share must be between 0 and 100');
      return;
    }
    setQaSaving(true);
    try {
      await axios.put(`${API}/pay-transfers/split-preset`, {
        from_driver_id: qa.from_driver_id,
        to_driver_id: qa.to_driver_id,
        co_percent: pct,
      }, { withCredentials: true });
      toast.success('Default split saved');
      setQa({ from_driver_id: '', to_driver_id: '', co_percent: '50' });
      setQaOpen(false);
      fetchPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save default split');
    } finally {
      setQaSaving(false);
    }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="pay-transfers-panel">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#002FA7]" />
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Team Pay Transfers <span className="font-normal text-slate-400">— optional</span>
          </h3>
        </div>
        <button
          type="button"
          onClick={autoFill}
          disabled={autoFilling}
          data-testid="transfer-auto-fill"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#002FA7] rounded-md px-3 py-1.5 hover:bg-[#00247D] disabled:opacity-50"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          title="Create a transfer for every team load without one, using each pair's saved split"
        >
          {autoFilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          Auto-fill from saved splits
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        Move a lump sum from a primary driver to a co-driver for this pay period. It appears as a deduction on the primary driver's paystub and a credit on the co-driver's. Add these before you generate.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end mb-4">
        <div className="md:col-span-4">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">From (primary — deducted)</label>
          <SearchableDriverSelect
            drivers={drivers}
            value={form.from_driver_id}
            onChange={(uid) => setForm((f) => ({ ...f, from_driver_id: uid }))}
            placeholder="Primary driver…"
            testid="transfer-from-select"
          />
        </div>
        <div className="md:col-span-4">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">To (co-driver — credited)</label>
          <SearchableDriverSelect
            drivers={drivers.filter((d) => d.user_id !== form.from_driver_id)}
            value={form.to_driver_id}
            onChange={(uid) => setForm((f) => ({ ...f, to_driver_id: uid }))}
            placeholder="Co-driver…"
            testid="transfer-to-select"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Amount ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            data-testid="transfer-amount-input"
            placeholder="0.00"
            className="w-full px-2.5 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          />
        </div>
        <div className="md:col-span-2">
          <button
            type="button"
            onClick={addTransfer}
            disabled={busy}
            data-testid="transfer-add-button"
            className="w-full flex items-center justify-center gap-1.5 bg-[#002FA7] text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
        <div className="md:col-span-12 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={suggestSplit}
            disabled={suggesting || !form.from_driver_id || !form.to_driver_id}
            data-testid="transfer-suggest-split"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-[#002FA7]/5 disabled:opacity-40"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Percent className="w-3.5 h-3.5" />}
            Suggest split
          </button>
          <span className="inline-flex items-center gap-1 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            co-driver gets
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={coPercent}
              onChange={(e) => setCoPercent(e.target.value)}
              data-testid="transfer-split-percent"
              className="w-14 px-2 py-1 text-xs border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
            %
          </span>
          <button
            type="button"
            onClick={savePreset}
            disabled={savingPreset || !form.from_driver_id || !form.to_driver_id}
            data-testid="transfer-save-preset"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-40"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            title="Remember this split for this driver pair"
          >
            {savingPreset ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Save as default
          </button>
          {suggestHint && (
            <span data-testid="transfer-suggest-hint" className="text-xs text-slate-500 w-full md:w-auto" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {suggestHint}
            </span>
          )}
        </div>
        <div className="md:col-span-12">
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            data-testid="transfer-note-input"
            placeholder="Note (optional) — e.g. 50/50 split of team loads"
            className="w-full px-2.5 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          />
        </div>
      </div>

      {presets.length > 0 && (
        <div className="mb-4" data-testid="split-presets-table">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Saved default splits</p>
            <button
              type="button"
              onClick={() => setQaOpen((v) => !v)}
              data-testid="preset-quick-add-toggle"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Plus className="w-3.5 h-3.5" /> New default split
            </button>
          </div>
          {qaOpen && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-3 p-3 rounded-md bg-slate-50 border border-slate-200" data-testid="preset-quick-add-form">
              <div className="md:col-span-5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Primary</label>
                <SearchableDriverSelect
                  drivers={drivers}
                  value={qa.from_driver_id}
                  onChange={(uid) => setQa((q) => ({ ...q, from_driver_id: uid }))}
                  placeholder="Primary driver…"
                  testid="qa-from-select"
                />
              </div>
              <div className="md:col-span-5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Co-driver</label>
                <SearchableDriverSelect
                  drivers={drivers.filter((d) => d.user_id !== qa.from_driver_id)}
                  value={qa.to_driver_id}
                  onChange={(uid) => setQa((q) => ({ ...q, to_driver_id: uid }))}
                  placeholder="Co-driver…"
                  testid="qa-to-select"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Co %</label>
                <input
                  type="number" min="0" max="100" step="1"
                  value={qa.co_percent}
                  onChange={(e) => setQa((q) => ({ ...q, co_percent: e.target.value }))}
                  data-testid="qa-percent-input"
                  className="w-full px-2 py-2 text-xs border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                />
              </div>
              <div className="md:col-span-1">
                <button
                  type="button" onClick={saveQuickPreset} disabled={qaSaving}
                  data-testid="qa-save-button"
                  className="w-full flex items-center justify-center bg-[#002FA7] text-white px-2 py-2 rounded-md text-xs font-semibold hover:bg-[#00247D] disabled:opacity-50"
                >
                  {qaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
          )}
          <ul className="space-y-1.5">
            {presets.map((p) => (
              <li key={p.id} data-testid={`preset-row-${p.id}`} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-md px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-800 min-w-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <span className="font-medium truncate">{p.from_name}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0" />
                  <span className="font-medium truncate">{p.to_name}</span>
                  <span className="flex-shrink-0 px-2 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] text-xs font-bold">co-driver {p.co_percent}%</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button type="button" onClick={() => editPreset(p)} data-testid={`preset-edit-${p.id}`} className="text-slate-500 hover:text-[#002FA7]" aria-label="Edit split default">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => deletePreset(p.id)} data-testid={`preset-delete-${p.id}`} className="text-red-600 hover:text-red-800" aria-label="Delete split default">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {presets.length === 0 && (
        <div className="mb-4" data-testid="split-presets-empty">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Saved default splits</p>
            <button
              type="button"
              onClick={() => setQaOpen((v) => !v)}
              data-testid="preset-quick-add-toggle"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Plus className="w-3.5 h-3.5" /> New default split
            </button>
          </div>
          {qaOpen ? (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-1 p-3 rounded-md bg-slate-50 border border-slate-200" data-testid="preset-quick-add-form">
              <div className="md:col-span-5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Primary</label>
                <SearchableDriverSelect drivers={drivers} value={qa.from_driver_id} onChange={(uid) => setQa((q) => ({ ...q, from_driver_id: uid }))} placeholder="Primary driver…" testid="qa-from-select" />
              </div>
              <div className="md:col-span-5">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Co-driver</label>
                <SearchableDriverSelect drivers={drivers.filter((d) => d.user_id !== qa.from_driver_id)} value={qa.to_driver_id} onChange={(uid) => setQa((q) => ({ ...q, to_driver_id: uid }))} placeholder="Co-driver…" testid="qa-to-select" />
              </div>
              <div className="md:col-span-1">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Co %</label>
                <input type="number" min="0" max="100" step="1" value={qa.co_percent} onChange={(e) => setQa((q) => ({ ...q, co_percent: e.target.value }))} data-testid="qa-percent-input" className="w-full px-2 py-2 text-xs border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
              </div>
              <div className="md:col-span-1">
                <button type="button" onClick={saveQuickPreset} disabled={qaSaving} data-testid="qa-save-button" className="w-full flex items-center justify-center bg-[#002FA7] text-white px-2 py-2 rounded-md text-xs font-semibold hover:bg-[#00247D] disabled:opacity-50">
                  {qaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No saved splits yet — add one so Suggest &amp; Auto-fill know each team's arrangement.</p>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-400" data-testid="transfers-loading">Loading transfers…</p>
      ) : transfers.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="transfers-empty" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          No transfers for this period yet.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="transfers-list">
          {transfers.map((t) => (
            <li key={t.id} data-testid={`transfer-row-${t.id}`} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-slate-800 min-w-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <span className="font-medium truncate">{t.from_driver_name || 'Driver'}</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0" />
                <span className="font-medium truncate">{t.to_driver_name || 'Driver'}</span>
                <span className="font-bold text-[#002FA7] flex-shrink-0">${Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                {t.note && <span className="text-xs text-slate-500 truncate">· {t.note}</span>}
              </div>
              <button
                type="button"
                onClick={() => removeTransfer(t.id)}
                data-testid={`transfer-delete-${t.id}`}
                className="text-red-600 hover:text-red-800 flex-shrink-0"
                aria-label="Delete transfer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PayTransfersPanel;
